import React, { useState } from 'react';
import '../App.css';

const AutoPilot = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);
  const [showTerminatedMessage, setShowTerminatedMessage] = useState(false);

  const smoothScrollTo = async (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const start = window.scrollY;
      const end = element.getBoundingClientRect().top + window.scrollY;
      const distance = end - start;
      const duration = Math.abs(distance) / 2; // Adjust duration based on distance
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1); // Clamp progress to 1
        const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // Easing function
        window.scrollTo(0, start + distance * easeInOutQuad(progress));

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      requestAnimationFrame(animateScroll);
    }
  };

  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const slowScrollToBottom = async (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const duration = 3000; // Total duration for scrolling
      const start = element.scrollTop; // Start position
      const end = element.scrollHeight - element.clientHeight; // End position
      const distance = end - start; // Total distance to scroll
      const startTime = performance.now(); // Record start time

      const animateScroll = (currentTime) => {
        const timeElapsed = currentTime - startTime; // Calculate elapsed time
        const progress = Math.min(timeElapsed / duration, 1); // Normalize progress to 1
        const easeInOutQuad = (t) => t < 0.1 ? 2 * t * t : -1 + (4 - 2 * t) * t; // Easing function
        element.scrollTop = start + distance * easeInOutQuad(progress); // Apply easing

        if (progress < 1) {
          requestAnimationFrame(animateScroll); // Continue animation
        }
      };
      requestAnimationFrame(animateScroll); // Start animation
    }
  };

  const openExperienceButtons = async () => {
    const buttons = document.querySelectorAll('#experience-section button');
    for (let button of buttons) {
      if (isTerminated) return; // Check if terminated
      button.click(); // Open the button
      await wait(2000); // Wait for 2 seconds to view the opened button
      if (isTerminated) return; // Check if terminated
      button.click(); // Close the button
      await wait(1000); // Wait for 1 second between each click for smooth transition
    }
  };

  const runAutoPilot = async () => {
    setIsRunning(true);
    setIsTerminated(false); // Reset termination state

    // Step 1: Go to expertise section and wait
    await smoothScrollTo('expertise');
    if (isTerminated) return; // Check if terminated
    await wait(3000); // Adjust wait time as necessary

    // Step 2: Go to work section and start slow scrolling instantly
    await smoothScrollTo('work-section');
    if (isTerminated) return; // Check if terminated
    await wait(500); // Wait for scroll to complete
    await slowScrollToBottom('work-section'); // Wait for scrolling to finish

    // Ensure scrolling in the work section has completed before moving on
    await wait(500); // Short wait to ensure smooth transition

    // Step 3: Go to experience section
    await smoothScrollTo('experience-section');
    if (isTerminated) return; // Check if terminated
    await wait(5000); // Wait for scroll to complete

    // Step 4: Open buttons in the experience section
    await openExperienceButtons();

    // Step 5: Go to contact section
    await smoothScrollTo('contact');

    setIsRunning(false);
  };

  const terminateAutoPilot = () => {
    setIsTerminated(true);
    setIsRunning(false);
    setShowTerminatedMessage(true);

    // Hide the message after 2 seconds
    setTimeout(() => {
      setShowTerminatedMessage(false);
    }, 2000);
  };

  return (
    <div>
      <button 
        id='clxsm'
        onClick={runAutoPilot}
        disabled={isRunning}
        className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span>{isRunning ? 'Auto-pilot Running...' : 'Start Auto-pilot'}</span>
        {isRunning && <span className="ml-2 spinner-border" role="status"></span>}
      </button>

      {isRunning && (
        <button 
          onClick={terminateAutoPilot}
          className="fixed bottom-4 right-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out"
        >
          Stop Auto-pilot
        </button>
      )}
      
      {showTerminatedMessage && (
        <div className="text-red-500 mt-2">Auto-pilot has been terminated.</div>
      )}
    </div>
  );
};

export default AutoPilot;
